import type { OAuthProviderId } from '../types';

// PKCE helpers for OAuth 2.0 Authorization Code Flow with PKCE
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const codeVerifier = generateRandomString(64);
  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64UrlEncode(hashed);
  return { codeVerifier, codeChallenge };
}

const OAUTH_CONFIG: Record<OAuthProviderId, { authUrl: string; clientId: string; scope: string }> = {
  google: {
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
    scope: 'openid email profile',
  },
  facebook: {
    authUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    clientId: import.meta.env.VITE_FACEBOOK_CLIENT_ID || '',
    scope: 'email,public_profile',
  },
  microsoft: {
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
    scope: 'openid email profile',
  },
  apple: {
    authUrl: 'https://appleid.apple.com/auth/authorize',
    clientId: import.meta.env.VITE_APPLE_CLIENT_ID || '',
    scope: 'name email',
  },
};

const REDIRECT_URI = `${window.location.origin}/auth/oauth/callback`;

export async function initiateOAuthFlow(provider: OAuthProviderId): Promise<void> {
  const config = OAUTH_CONFIG[provider];
  if (!config.clientId) {
    throw new Error(`OAuth client ID not configured for ${provider}`);
  }

  const { codeVerifier, codeChallenge } = await generatePKCE();

  // Store PKCE verifier and provider in sessionStorage for callback
  sessionStorage.setItem('oauth_code_verifier', codeVerifier);
  sessionStorage.setItem('oauth_provider', provider);

  const state = generateRandomString(32);
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: config.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${config.authUrl}?${params.toString()}`;
}

export function getOAuthCallbackData(): { code: string; provider: OAuthProviderId; codeVerifier: string } | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const storedState = sessionStorage.getItem('oauth_state');
  const provider = sessionStorage.getItem('oauth_provider') as OAuthProviderId | null;
  const codeVerifier = sessionStorage.getItem('oauth_code_verifier');

  if (!code || !state || !storedState || state !== storedState || !provider || !codeVerifier) {
    return null;
  }

  // Clean up
  sessionStorage.removeItem('oauth_code_verifier');
  sessionStorage.removeItem('oauth_provider');
  sessionStorage.removeItem('oauth_state');

  return { code, provider, codeVerifier };
}
